import { useEffect, useRef, useState } from "react"
import LanguageSheet from "./LanguageSheet"
import SyncStatusChip from "./SyncStatusChip"
import useCloseRequest from "../hooks/useCloseRequest"
import "./AppHeader.css"

export default function AppHeader({
  title,
  onOpenSearch,
  onOpenOfflineData,
  restaurant,
  user,
  languages = [],
  language,
  onChangeLanguage,
  onSwitchRestaurant,
  onSignOut
}) {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isLanguageOpen, setIsLanguageOpen] = useState(false)
  const accountRef = useRef(null)

  // One language is not a choice — the row disappears rather than offering it.
  const canChooseLanguage = languages.length > 1
  const currentLanguage = languages.find((entry) => entry.code === language)

  useEffect(() => {
    if (!isMenuOpen) return undefined
    const closeOnOutsideClick = (event) => {
      if (!accountRef.current?.contains(event.target)) setIsMenuOpen(false)
    }
    document.addEventListener("pointerdown", closeOnOutsideClick)
    return () => document.removeEventListener("pointerdown", closeOnOutsideClick)
  }, [isMenuOpen])

  useCloseRequest(isMenuOpen, () => setIsMenuOpen(false))

  return (
    <header className="app-header">
      <h1 className="app-header__title t-sb24">{title}</h1>

      <div className="app-header__actions">
        <SyncStatusChip onClick={onOpenOfflineData} />

        <button
          type="button"
          className="app-header__icon-button"
          aria-label="Search menu"
          onClick={onOpenSearch}
        >
          <SearchIcon />
        </button>

        <div className="app-header__account" ref={accountRef}>
          <button
            type="button"
            className="app-header__icon-button"
            aria-label="Account"
            aria-expanded={isMenuOpen}
            onClick={() => setIsMenuOpen((open) => !open)}
          >
            <AccountIcon />
          </button>

          {isMenuOpen && (
            <div className="account-menu" role="menu">
              <div className="account-menu__identity">
                <p className="account-menu__name t-s14">{user?.fullName || user?.email}</p>
                {restaurant?.name && (
                  <p className="account-menu__restaurant t-p12">{restaurant.name}</p>
                )}
              </div>
              <button
                type="button"
                role="menuitem"
                className="account-menu__item t-p14"
                onClick={() => {
                  setIsMenuOpen(false)
                  onOpenOfflineData()
                }}
              >
                Offline Data
              </button>
              <button
                type="button"
                role="menuitem"
                className="account-menu__item t-p14"
                onClick={() => {
                  setIsMenuOpen(false)
                  onSwitchRestaurant()
                }}
              >
                Switch Restaurant
              </button>
              {canChooseLanguage && (
                <button
                  type="button"
                  role="menuitem"
                  className="account-menu__item t-p14"
                  onClick={() => {
                    setIsMenuOpen(false)
                    setIsLanguageOpen(true)
                  }}
                >
                  Language
                  <span className="account-menu__value" lang={currentLanguage?.code}>
                    {currentLanguage?.native_name ?? ""}
                  </span>
                </button>
              )}
              <button
                type="button"
                role="menuitem"
                className="account-menu__item account-menu__item--danger t-p14"
                onClick={() => {
                  setIsMenuOpen(false)
                  onSignOut()
                }}
              >
                Log Out
              </button>
            </div>
          )}
        </div>
      </div>

      {isLanguageOpen && (
        <LanguageSheet
          languages={languages}
          current={language}
          onSave={(code) => {
            setIsLanguageOpen(false)
            onChangeLanguage(code)
          }}
          onClose={() => setIsLanguageOpen(false)}
        />
      )}
    </header>
  )
}

function SearchIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="1.8" />
      <path d="m16.5 16.5 4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  )
}

function AccountIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="8.5" r="3.75" stroke="currentColor" strokeWidth="1.8" />
      <path
        d="M4.75 20c.9-3.6 3.75-5.5 7.25-5.5s6.35 1.9 7.25 5.5"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  )
}
